<!--
  NoticeModal:固定结构提示弹窗
  - H5 / PC 均为居中 modal
  - 结构:可选图片 + 标题 + 描述 + 扩展内容 + 操作区
  - buttonUrl 为空时主按钮关闭弹窗;有值时跳转
  - 独立实现遮罩、teleport、背景滚动锁定,不依赖 PanelModal
-->
<template>
  <div
    v-if="props.visible"
    class="notice-modal-mask"
    :class="[
      `page-${props.pageType}`,
      `variant-${props.variant}`,
    ]"
    :style="{ zIndex }"
    @click.self="onMaskClick"
  >
    <div
      class="notice-modal-stack"
      :class="[`page-${props.pageType}`]"
    >
      <section
        class="notice-modal-panel"
        :class="[
          `page-${props.pageType}`,
          `variant-${props.variant}`,
        ]"
        role="dialog"
        aria-modal="true"
        :aria-label="props.title || props.description || ''"
      >
        <img
          v-if="props.imageSrc"
          class="notice-modal-image"
          :class="`image-${props.imagePreset}`"
          :src="props.imageSrc"
          :alt="props.imageAlt"
        />

        <div
          class="notice-modal-copy"
          :class="`align-${props.textAlign}`"
        >
          <h2 v-if="props.title" class="notice-modal-title">{{ props.title }}</h2>
          <p
            v-if="props.description"
            class="notice-modal-description"
          >
            {{ props.description }}
          </p>
        </div>

        <div
          v-if="$slots.default"
          class="notice-modal-extra"
        >
          <slot />
        </div>

        <div
          v-if="$slots.actions || props.buttonText"
          class="notice-modal-actions"
        >
          <slot name="actions">
            <button
              type="button"
              class="notice-modal-primary-button"
              @click="onConfirm"
            >
              {{ props.buttonText }}
            </button>
          </slot>
        </div>
      </section>

      <button
        v-if="props.showCloseButton"
        type="button"
        class="notice-modal-close-button"
        :aria-label="props.closeButtonLabel"
        @click="$emit('close')"
      >
        <template v-if="props.pageType === 'PC'">
          <span class="notice-modal-close-line line-left" />
          <span class="notice-modal-close-line line-right" />
        </template>
        <template v-else>
          <img
            class="notice-modal-close-icon"
            :src="closeCircleIcon"
            alt=""
          />
        </template>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import type { ComputedRef } from 'vue-demi';
import closeCircleIcon from '../assets/icon-notice-close-circle.svg';
type WebapiUse = {
  useTeleport: (target: HTMLElement) => void;
  useLockScreen: (locked: ComputedRef<boolean>) => void;
  useZIndex: () => number;
};

// Vue2 FEGO 运行时惯例:require(...).use 避免 ESM interop 差异

type PageType = 'PC' | 'H5';
type NoticeVariant = 'notice' | 'confirm' | 'alert';
type TextAlign = 'center' | 'left';
type ImagePreset = 'default' | 'moomoo' | 'book';
type Props = {
  visible: boolean;
  pageType: PageType;
  imageSrc?: string;
  imageAlt?: string;
  imagePreset?: ImagePreset;
  title: string;
  description?: string;
  buttonText?: string;
  buttonUrl?: string;
  variant?: NoticeVariant;
  textAlign?: TextAlign;
  closeOnMask?: boolean;
  showCloseButton?: boolean;
  closeButtonLabel?: string;
};
const __sfc_main = {};
__sfc_main.props = {
  visible: {
    key: "visible",
    required: true,
    type: Boolean
  },
  pageType: {
    key: "pageType",
    required: true,
    type: null
  },
  imageSrc: {
    key: "imageSrc",
    required: false,
    type: String,
    default: ''
  },
  imageAlt: {
    key: "imageAlt",
    required: false,
    type: String,
    default: ''
  },
  imagePreset: {
    key: "imagePreset",
    required: false,
    type: null,
    default: 'default'
  },
  title: {
    key: "title",
    required: true,
    type: String
  },
  description: {
    key: "description",
    required: false,
    type: String,
    default: ''
  },
  buttonText: {
    key: "buttonText",
    required: false,
    type: String,
    default: ''
  },
  buttonUrl: {
    key: "buttonUrl",
    required: false,
    type: String,
    default: ''
  },
  variant: {
    key: "variant",
    required: false,
    type: null,
    default: 'notice'
  },
  textAlign: {
    key: "textAlign",
    required: false,
    type: null,
    default: 'center'
  },
  closeOnMask: {
    key: "closeOnMask",
    required: false,
    type: Boolean,
    default: true
  },
  showCloseButton: {
    key: "showCloseButton",
    required: false,
    type: Boolean,
    default: true
  },
  closeButtonLabel: {
    key: "closeButtonLabel",
    required: false,
    type: String,
    default: 'Close'
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const {
    useTeleport,
    useLockScreen,
    useZIndex
  } = (require('@fegobull/webapi') as {
    use: WebapiUse;
  }).use;
  const props = __props;
  const emit = __ctx.emit;

  // 弹层挂到 body 下,避免宿主容器 overflow/transform 裁切
  useTeleport(document.body);
  const zIndex = useZIndex();

  // visible 为真时锁定背景滚动,关闭后自动恢复
  useLockScreen(computed(() => props.visible));
  function onMaskClick(): void {
    if (props.closeOnMask) {
      emit('close');
    }
  }
  function onConfirm(): void {
    emit('confirm');
    const buttonUrl = props.buttonUrl.trim();
    if (buttonUrl.length === 0) {
      emit('close');
      return;
    }
    window.location.href = buttonUrl;
  }
  return {
    closeCircleIcon,
    props,
    zIndex,
    onMaskClick,
    onConfirm
  };
};
export default __sfc_main;
</script>

<style scoped>
.notice-modal-mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notice-modal-mask.variant-notice {
  background: rgba(38, 38, 38, 0.96);
}

.notice-modal-mask.variant-confirm,
.notice-modal-mask.variant-alert {
  background: rgba(0, 0, 0, 0.5);
}

.notice-modal-stack {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.notice-modal-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  background: #ffffff;
}

.notice-modal-image {
  display: block;
  object-fit: contain;
}

.notice-modal-copy {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  box-sizing: border-box;
}

.notice-modal-copy.align-center {
  align-items: center;
}

.notice-modal-copy.align-left {
  align-items: flex-start;
}

.notice-modal-extra,
.notice-modal-actions {
  align-self: stretch;
}

.notice-modal-actions {
  display: flex;
  justify-content: center;
}

.notice-modal-title,
.notice-modal-description {
  margin: 0;
}

.notice-modal-copy.align-center .notice-modal-title,
.notice-modal-copy.align-center .notice-modal-description {
  text-align: center;
}

.notice-modal-copy.align-left .notice-modal-title,
.notice-modal-copy.align-left .notice-modal-description {
  text-align: left;
}

.notice-modal-title {
  color: #0f1112;
  font-weight: 520;
}

.notice-modal-description {
  color: #626262;
  font-weight: 400;
}

.notice-modal-primary-button {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  border: none;
  background: #ff6900;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
}

.notice-modal-close-button {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.notice-modal-close-icon {
  display: block;
  width: 100%;
  height: 100%;
}

/* H5:设计稿 100px = 1rem */
.notice-modal-panel.page-H5.variant-notice {
  width: 6.7rem;
  padding: 0.6rem 0.4rem 0.4rem;
  gap: 0.6rem;
  border-radius: 0.32rem;
}

.notice-modal-panel.page-H5.variant-confirm {
  width: 6.7rem;
  padding: 0.5rem 0.4rem;
  gap: 0.32rem;
  border-radius: 0.32rem;
}

.notice-modal-panel.page-H5.variant-alert {
  width: 6.7rem;
  padding: 0.4rem;
  gap: 0.4rem;
  border-radius: 0.32rem;
}

.page-H5 .notice-modal-image {
  max-width: 4.8rem;
  max-height: 2.77rem;
}

.page-H5 .notice-modal-image.image-moomoo {
  width: auto;
  max-height: 2.77rem;
}

.page-H5 .notice-modal-image.image-book {
  width: auto;
  max-width: 2.2rem;
  max-height: 1.7rem;
}

.page-H5 .notice-modal-copy {
  gap: 0.24rem;
}

.page-H5 .notice-modal-title {
  font-size: 0.4231rem;
  line-height: 1.326;
}

.page-H5.variant-confirm .notice-modal-title {
  font-weight: 600;
  font-size: 0.5rem;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: #262626;
}

.page-H5 .notice-modal-description {
  width: 5.9rem;
  font-size: 0.3rem;
  line-height: 1.4;
}

.page-H5 .notice-modal-primary-button {
  width: 4.5rem;
  height: 0.9rem;
  border-radius: 2rem;
  font-size: 0.36rem;
  line-height: 1.35;
}

.page-H5 .notice-modal-close-button {
  width: 0.6rem;
  height: 0.6rem;
  top: calc(100% + 0.5rem);
}

/* PC:716:19688 / 716:19732 视觉稿 modal,width 510,padding 40/30 */
.notice-modal-panel.page-PC.variant-notice,
.notice-modal-panel.page-PC.variant-alert {
  width: 510px;
  padding: 40px 30px;
  border-radius: 20px;
}

.notice-modal-panel.page-PC.variant-notice {
  gap: 24px;
}

.notice-modal-panel.page-PC.variant-alert {
  gap: 20px;
}

.notice-modal-panel.page-PC.variant-confirm {
  width: 550px;
  padding: 40px 30px;
  border-radius: 20px;
}

.page-PC .notice-modal-image {
  max-width: 288px;
  max-height: 166.2px;
}

.page-PC .notice-modal-image.image-moomoo {
  width: auto;
  max-height: 166.2px;
}

.page-PC .notice-modal-image.image-book {
  width: auto;
  max-width: 140px;
  max-height: 108px;
}

.page-PC .notice-modal-copy {
  gap: 10px;
}

.page-PC.variant-confirm .notice-modal-copy {
  gap: 0;
}

.page-PC.variant-confirm .notice-modal-description {
  margin-top: 4px;
  align-self: stretch;
}

.page-PC.variant-confirm .notice-modal-extra {
  margin-top: 20px;
}

.page-PC .notice-modal-title {
  font-size: 28px;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.page-PC.variant-confirm .notice-modal-title {
  font-size: 28px;
  font-weight: 600;
  color: #000000;
}

.page-PC .notice-modal-description {
  font-size: 18px;
  line-height: 1.4;
}

.page-PC .notice-modal-primary-button {
  width: 280px;
  height: 60px;
  border-radius: 60px;
  font-size: 24px;
  line-height: 1.2;
}

.page-PC .notice-modal-close-button {
  width: 48px;
  height: 48px;
  top: calc(100% + 24px);
  border: 1.5px solid #ffffff;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
}

.notice-modal-close-line {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 1.5px;
  background: #ffffff;
  transform-origin: center;
}

.notice-modal-close-line.line-left {
  transform: translate(-50%, -50%) rotate(45deg);
}

.notice-modal-close-line.line-right {
  transform: translate(-50%, -50%) rotate(-45deg);
}
</style>
